import React, { useState, useCallback, useMemo } from "react"
import { 
    Button,
    ListItem,
    ListItemText,
    Typography,
    Box,
    IconButton,
    Grid,
    Paper,
    List,
    Menu,
    MenuItem,
    Fab,
    Tooltip,
    TextField,
    InputAdornment,
    Popover,
    Alert,
    Chip,
    Checkbox,
    FormControlLabel
} from "@mui/material"
import { 
    FormatQuote,
    Add as AddIcon, 
    Check as CheckIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    Warning as WarningIcon,
    Help as HelpIcon
} from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'
import { userRequest } from '../requestMethods';
import TranslationPhraseEditorGuide from "./UserGuides/TranslationPhraseEditorGuide"
import FullScreenDialog from "./FullScreenDialog"

// Color palette for phrase mapping
const PHRASE_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85929E', '#A569BD',
    '#5DADE2', '#58D68D', '#F4D03F', '#EC7063', '#AED6F1'
];

// Helper function to generate color for a phrase
const generatePhraseColor = (phraseIndex) => {
    return PHRASE_COLORS[phraseIndex % PHRASE_COLORS.length];
};

// Helper function to check if two position ranges overlap
const doPositionsOverlap = (pos1, pos2) => {
    return pos1.left < pos2.right && pos2.left < pos1.right;
};

// Helper function to detect conflicts between phrases
const detectPhraseConflicts = (phraseObjects) => {
    const conflicts = [];
    
    for (let i = 0; i < phraseObjects.length; i++) {
        for (let j = i + 1; j < phraseObjects.length; j++) {
            const phrase1 = phraseObjects[i];
            const phrase2 = phraseObjects[j];
            
            // Check if any mapping positions overlap
            const hasOverlap = phrase1.mapping_position.some(pos1 => 
                phrase2.mapping_position.some(pos2 => doPositionsOverlap(pos1, pos2))
            );
            
            if (hasOverlap) {
                conflicts.push({
                    phrase1Index: i,
                    phrase2Index: j,
                    phrase1: phrase1.phrase,
                    phrase2: phrase2.phrase
                });
            }
        }
    }
    
    return conflicts;
};

// Separate component for translation input to prevent re-rendering the entire list
const TranslationInput = React.memo(({ initialValue, dictionaryId, onTranslationChange, phrase }) => {
    const [value, setValue] = useState(initialValue || "")
    const [timeoutId, setTimeoutId] = useState(null)
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTranslations, setSelectedTranslations] = useState(() => {
        const parts = (initialValue || '').split(',').map(s => s.trim()).filter(Boolean)
        return parts
    });

    // Sync when initial value changes from parent
    React.useEffect(() => {
        const parts = (initialValue || '').split(',').map(s => s.trim()).filter(Boolean)
        setSelectedTranslations(parts)
        setValue(initialValue || '')
    }, [initialValue])

    const handleChange = (e) => {
        setValue(e.target.value)
        const parts = (e.target.value || '').split(',').map(s => s.trim()).filter(Boolean)
        setSelectedTranslations(parts)
        
        // Clear existing timeout
        if (timeoutId) {
            clearTimeout(timeoutId)
        }

        // Set new timeout to update parent after 300ms of no typing
        const newTimeoutId = setTimeout(() => {
            onTranslationChange(e.target.value, dictionaryId)
        }, 300)
        
        setTimeoutId(newTimeoutId)
    }

    const handleSearchClick = (event) => {
        setAnchorEl(event.currentTarget);
        // Set search query to the current phrase and trigger search
        const phraseToSearch = phrase.trim();
        setSearchQuery(phraseToSearch);
        if (phraseToSearch) {
            handleSearch(phraseToSearch);
        }
    };

    const handleSearchClose = () => {
        setAnchorEl(null);
        setSearchQuery("");
        setSearchResults([]);
    };

    const handleSearch = async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        try {
            const response = await userRequest.get(`/dictionaryPhrases/search/${query}`);
            setSearchResults(response.data);
        } catch (error) {
            console.error('Error searching phrases:', error);
            setSearchResults([]);
        }
    };

    const handleSearchQueryChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        
        // Debounce search
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        const newTimeoutId = setTimeout(() => {
            handleSearch(query);
        }, 300);
        setTimeoutId(newTimeoutId);
    };

    const handleToggleTranslationSelect = (translationValue) => {
        setSelectedTranslations(prev => {
            let next
            if (prev.includes(translationValue)) {
                next = prev.filter(v => v !== translationValue)
            } else {
                next = [...prev, translationValue]
            }
            const joined = next.join(', ')
            setValue(joined)
            onTranslationChange(joined, dictionaryId)
            return next
        })
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <input
                type="text"
                value={value}
                onChange={handleChange}
                placeholder="Enter translation"
                style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}
            />
            <IconButton onClick={handleSearchClick} size="small">
                <SearchIcon />
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleSearchClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, width: 400 }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                        placeholder="Search phrases..."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ mt: 2, maxHeight: 300, overflowY: 'auto' }}>
                        {searchResults.map((phrase) => (
                            <Box 
                                key={phrase._id}
                                sx={{
                                    p: 1,
                                    '&:hover': { bgcolor: 'action.hover' },
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {phrase.phrase}
                                </Typography>
                                {phrase.translations.map((translation, idx) => (
                                    <Box 
                                        key={`${phrase._id}-${idx}`}
                                        sx={{ 
                                            pl: 1,
                                            py: 0.25,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedTranslations.includes(translation.value)}
                                                    onChange={() => handleToggleTranslationSelect(translation.value)}
                                                />
                                            }
                                            label={`${translation.language?.name}: ${translation.value}`}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ))}
                        {searchResults.length === 0 && searchQuery && (
                            <Typography variant="body2" sx={{ p: 1, color: 'text.secondary' }}>
                                No results found
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Popover>
        </Box>
    )
})

function TranslationPhraseEditor({ 
    phraseObjects, 
    phrasesDictionary, 
    onPhrasesDictionaryChange,
    onPhraseObjectsChange,
    text,
    wordObjects
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedPhraseIndex, setSelectedPhraseIndex] = useState(null)
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const [phraseIndexForMenu, setPhraseIndexForMenu] = useState(null)
    
    // States for phrase creation
    const [isCreatingPhrase, setIsCreatingPhrase] = useState(false)
    const [selectedWordIndices, setSelectedWordIndices] = useState([])
    
    // User Guide state
    const [userGuideOpen, setUserGuideOpen] = useState(false)

    // Memoized conflict detection and color mapping
    const conflictData = useMemo(() => {
        const conflicts = detectPhraseConflicts(phraseObjects);
        const conflictingPhraseIndices = new Set();
        
        conflicts.forEach(conflict => {
            conflictingPhraseIndices.add(conflict.phrase1Index);
            conflictingPhraseIndices.add(conflict.phrase2Index);
        });
        
        return { conflicts, conflictingPhraseIndices };
    }, [phraseObjects]);

    // Memoized color mapping for phrases
    const phraseColors = useMemo(() => {
        const colorMap = new Map();
        phraseObjects.forEach((phrase, index) => {
            colorMap.set(index, generatePhraseColor(index));
        });
        return colorMap;
    }, [phraseObjects]);

    // Helper function to get text positions that should be highlighted for a specific phrase
    const getHighlightedPositions = useMemo(() => {
        const positionMap = new Map();
        
        phraseObjects.forEach((phrase, phraseIndex) => {
            phrase.mapping_position.forEach(pos => {
                // Create multiple keys for flexible matching
                const exactKey = `${pos.left}-${pos.right}`;
                // const leftKey = `${pos.left}`; // not used currently
                
                // Store both exact and left position for matching
                if (!positionMap.has(exactKey)) {
                    positionMap.set(exactKey, []);
                }
                
                positionMap.get(exactKey).push({
                    phraseIndex,
                    color: phraseColors.get(phraseIndex),
                    isConflicting: conflictData.conflictingPhraseIndices.has(phraseIndex),
                    originalPosition: pos
                });
            });
        });
        
        return positionMap;
    }, [phraseObjects, phraseColors, conflictData]);

    // Helper function to check if a word position matches any phrase position
    const findMatchingPhraseInfo = (wordObj) => {
        const wordLeft = wordObj.mapping_position.left;
        const wordRight = wordObj.mapping_position.right;
        
        // First try exact match
        const exactKey = `${wordLeft}-${wordRight}`;
        let highlightInfo = getHighlightedPositions.get(exactKey);
        
        if (highlightInfo && highlightInfo.length > 0) {
            return highlightInfo;
        }
        
        // If no exact match, check if this word falls within any phrase position range
        for (let phraseIndex = 0; phraseIndex < phraseObjects.length; phraseIndex++) {
            const phrase = phraseObjects[phraseIndex];
            
            // Check if word position overlaps with any phrase position
            const isInPhrase = phrase.mapping_position.some(phrasePos => {
                // Check if word position overlaps with phrase position
                return (wordLeft >= phrasePos.left && wordLeft < phrasePos.right) ||
                       (wordRight > phrasePos.left && wordRight <= phrasePos.right) ||
                       (wordLeft <= phrasePos.left && wordRight >= phrasePos.right);
            });
            
            if (isInPhrase) {
                return [{
                    phraseIndex,
                    color: phraseColors.get(phraseIndex),
                    isConflicting: conflictData.conflictingPhraseIndices.has(phraseIndex)
                }];
            }
        }
        
        return null;
    };

    const handleDictionaryItemSelect = (dictionaryId) => {
        if (phraseIndexForMenu === null) return

        const updatedPhraseObjects = [...phraseObjects]
        updatedPhraseObjects[phraseIndexForMenu] = {
            ...updatedPhraseObjects[phraseIndexForMenu],
            dictionaryPhraseId: dictionaryId
        }
        onPhraseObjectsChange(updatedPhraseObjects)
        setMenuAnchorEl(null)
        setPhraseIndexForMenu(null)
    }

    const getTranslationForPhrase = (phraseObj) => {
        if (!phraseObj.dictionaryPhraseId) return "Click to map to dictionary"
        const dictEntry = phrasesDictionary.get(phraseObj.dictionaryPhraseId)
        return dictEntry?.translation || "No translation"
    }

    const handlePhraseItemClick = (index) => {
        setSelectedPhraseIndex(index === selectedPhraseIndex ? null : index)
    }

    const handleTranslationClick = (event, index) => {
        event.stopPropagation()
        setMenuAnchorEl(event.currentTarget)
        setPhraseIndexForMenu(index)
    }

    const handleDictionaryChange = useCallback((newTranslation, dictionaryId) => {
        const updatedDictionary = new Map(phrasesDictionary)
        const dictEntry = updatedDictionary.get(dictionaryId)
        if (dictEntry) {
            updatedDictionary.set(dictionaryId, {
                ...dictEntry,
                translation: newTranslation
            })
            onPhrasesDictionaryChange(updatedDictionary)
        }
    }, [phrasesDictionary, onPhrasesDictionaryChange])

    const handlePhraseChange = useCallback((newPhrase, dictionaryId) => {
        const updatedDictionary = new Map(phrasesDictionary)
        const dictEntry = updatedDictionary.get(dictionaryId)
        if (dictEntry) {
            updatedDictionary.set(dictionaryId, {
                ...dictEntry,
                phrase: newPhrase
            })
            onPhrasesDictionaryChange(updatedDictionary)
        }
    }, [phrasesDictionary, onPhrasesDictionaryChange])

    // New functions for phrase creation
    const handleWordClick = (index) => {
        if (!isCreatingPhrase) return

        setSelectedWordIndices(prev => {
            // If already selected, remove it
            if (prev.includes(index)) {
                return prev.filter(i => i !== index)
            }
            // Add new index in sorted order
            const newIndices = [...prev, index].sort((a, b) => a - b)
            return newIndices
        })
    }

    const handleDeletePhrase = (index, event) => {
        event.stopPropagation()
        const updatedPhrases = [...phraseObjects]
        updatedPhrases.splice(index, 1)
        onPhraseObjectsChange(updatedPhrases)
    }

    const handleDeleteDictionaryEntry = (id, event) => {
        event.stopPropagation()
        const updatedDictionary = new Map(phrasesDictionary)
        updatedDictionary.delete(id)
        
        // Also update any phrase objects that were mapped to this dictionary entry
        const updatedPhrases = phraseObjects.map(phrase => {
            if (phrase.dictionaryPhraseId === id) {
                return { ...phrase, dictionaryPhraseId: '' }
            }
            return phrase
        })
        
        onPhrasesDictionaryChange(updatedDictionary)
        onPhraseObjectsChange(updatedPhrases)
    }

    const handleMapPhrasesToDictionary = () => {
        const updatedPhraseObjects = phraseObjects.map(phraseObj => {
            // Try to find matching dictionary entry
            for (let [id, dictPhrase] of phrasesDictionary.entries()) {
                if (phraseObj.phrase.toLowerCase() === dictPhrase.phrase.toLowerCase()) {
                    return {
                        ...phraseObj,
                        dictionaryPhraseId: id
                    };
                }
            }
            // If no match found, keep the existing dictionaryPhraseId if it exists
            return phraseObj;
        });

        onPhraseObjectsChange(updatedPhraseObjects);
    }

    const createNewPhrase = () => {
        if (selectedWordIndices.length === 0) return

        // Get the selected word objects
        const selectedWords = selectedWordIndices.map(index => wordObjects[index])
        
        // Join lemmas
        const phraseLemma = selectedWords.map(word => word.lemma).join(' ')
        
        // Get the first and last positions for mapping
        const mappingPositions = selectedWords.map(word => ({
            left: word.mapping_position.left,
            right: word.mapping_position.right
        }))

        // Create new dictionary entry if it doesn't exist
        let dictionaryId = ''
        const existingEntry = Array.from(phrasesDictionary.entries())
            .find(([_, entry]) => entry.phrase === phraseLemma)

        if (existingEntry) {
            dictionaryId = existingEntry[0]
        } else {
            dictionaryId = uuidv4()
            const updatedDictionary = new Map(phrasesDictionary)
            updatedDictionary.set(dictionaryId, {
                id: dictionaryId,
                phrase: phraseLemma,
                translation: ''
            })
            onPhrasesDictionaryChange(updatedDictionary)
        }

        // Create new phrase object
        const newPhrase = {
            phrase: phraseLemma,
            mapping_position: mappingPositions,
            dictionaryPhraseId: dictionaryId // Automatically map to dictionary
        }

        // Add to phraseObjects
        onPhraseObjectsChange([...phraseObjects, newPhrase])

        // Reset selection state
        setSelectedWordIndices([])
        setIsCreatingPhrase(false)
    }

    const renderFormattedText = () => {
        if (!text || !wordObjects) return null

        return (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, mb: 2 }}>
                {/* Conflict Detection Warnings */}
                {conflictData.conflicts.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon />
                            <Typography variant="subtitle2">
                                {conflictData.conflicts.length} phrase conflict(s) detected:
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            {conflictData.conflicts.map((conflict, idx) => (
                                <Chip 
                                    key={idx}
                                    label={`"${conflict.phrase1}" ↔ "${conflict.phrase2}"`}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1, mb: 1 }}
                                />
                            ))}
                        </Box>
                    </Alert>
                )}

                <Typography variant="h6" gutterBottom>
                    Text
                    {isCreatingPhrase && (
                        <Typography component="span" color="primary" sx={{ ml: 2 }}>
                            (Click words to create a phrase)
                        </Typography>
                    )}
                </Typography>

                {/* Color Legend for Phrases */}
                {phraseObjects.length > 0 && (
                    <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Phrase Colors:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {phraseObjects.map((phrase, index) => (
                                <Chip 
                                    key={index}
                                    label={phrase.phrase}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: phraseColors.get(index),
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        border: conflictData.conflictingPhraseIndices.has(index) 
                                            ? '2px solid #ff5722' 
                                            : 'none'
                                    }}
                                    icon={conflictData.conflictingPhraseIndices.has(index) ? 
                                        <WarningIcon sx={{ color: '#fff !important' }} /> : 
                                        undefined
                                    }
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <Box sx={{ 
                    lineHeight: 2,
                    '& .word': {
                        cursor: isCreatingPhrase ? 'pointer' : 'default',
                        position: 'relative',
                        '&:hover': isCreatingPhrase ? {
                            bgcolor: 'action.hover',
                            borderRadius: 0.5
                        } : {},
                        '&.selected': {
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                            borderRadius: 0.5,
                            fontWeight: 'bold'
                        },
                        '&.phrase-highlighted': {
                            borderRadius: 0.5,
                            fontWeight: 'bold',
                            color: '#fff',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                        },
                        '&.phrase-conflicting': {
                            border: '2px solid #ff5722',
                            animation: 'pulse 2s infinite'
                        }
                    },
                    '@keyframes pulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(255, 87, 34, 0.7)' },
                        '70%': { boxShadow: '0 0 0 4px rgba(255, 87, 34, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(255, 87, 34, 0)' }
                    }
                }}>
                    {wordObjects.map((wordObj, index) => {
                        const isSelected = selectedWordIndices.includes(index)
                        
                        // Check if this word is part of any phrase using improved matching
                        const highlightInfo = findMatchingPhraseInfo(wordObj);
                        
                        let wordStyle = {};
                        let className = 'word';
                        
                        if (isSelected) {
                            className += ' selected';
                        } else if (highlightInfo && highlightInfo.length > 0) {
                            // Use the first phrase's color if multiple phrases overlap
                            const firstPhrase = highlightInfo[0];
                            wordStyle.backgroundColor = firstPhrase.color;
                            className += ' phrase-highlighted';
                            
                            if (firstPhrase.isConflicting) {
                                className += ' phrase-conflicting';
                            }
                        }
                        
                        return (
                            <React.Fragment key={index}>
                                {/* Text before the word */}
                                {index === 0 ? 
                                    text.slice(0, wordObj.positions.left) : 
                                    text.slice(wordObjects[index - 1].positions.right, wordObj.positions.left)
                                }
                                {/* The word itself */}
                                <span 
                                    className={className}
                                    style={wordStyle}
                                    onClick={() => handleWordClick(index)}
                                    title={highlightInfo ? 
                                        highlightInfo.map((info, idx) => 
                                            `Phrase ${info.phraseIndex + 1}: ${phraseObjects[info.phraseIndex]?.phrase}`
                                        ).join(', ') : 
                                        undefined
                                    }
                                >
                                    {text.slice(wordObj.positions.left, wordObj.positions.right)}
                                </span>
                                {/* Text after the last word */}
                                {index === wordObjects.length - 1 && 
                                    text.slice(wordObj.positions.right)
                                }
                            </React.Fragment>
                        )
                    })}
                </Box>
            </Box>
        )
    }

    return (
        <>
            <Button 
                variant="outlined"
                size="small"
                startIcon={<FormatQuote />}
                endIcon={phraseObjects?.length > 0 ? <Chip label="✓" size="small" color="success" sx={{ ml: 0.5, minWidth: 24 }} /> : null}
                onClick={() => setIsOpen(true)}
                disabled={!text || !wordObjects || wordObjects.length === 0}
                title="Opens phrases dictionary editor"
            >
                Phrases dictionary
            </Button>

            <FullScreenDialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                title="Phrases Editor"
                actions={
                    <Button 
                        color="inherit"
                        onClick={handleMapPhrasesToDictionary}
                    >
                        Map Phrases to Dictionary
                    </Button>
                }
                helpAction={
                    <IconButton
                        color="inherit"
                        onClick={() => setUserGuideOpen(true)}
                        sx={{ mr: 1 }}
                        title="How to use Phrases Editor"
                    >
                        <HelpIcon />
                    </IconButton>
                }
            >
                {/* Text section with word selection */}
                {renderFormattedText()}

                <Grid container sx={{ height: 'calc(100% - 64px - 200px)' }}>
                    {/* Left side - Phrases List */}
                    <Grid item xs={6} sx={{ borderRight: 1, borderColor: 'divider', height: '100%', overflow: 'auto' }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Detected Phrases
                            </Typography>
                            <List>
                                {phraseObjects.map((phrase, index) => {
                                    const phraseColor = phraseColors.get(index);
                                    const isConflicting = conflictData.conflictingPhraseIndices.has(index);
                                    
                                    return (
                                        <ListItem 
                                            key={index}
                                            onClick={() => handlePhraseItemClick(index)}
                                            sx={{ 
                                                border: isConflicting ? '2px solid #ff5722' : '1px solid #e0e0e0',
                                                mb: 1,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                bgcolor: selectedPhraseIndex === index ? 'action.selected' : 'background.paper',
                                                borderLeft: `6px solid ${phraseColor}`,
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <Box 
                                                sx={{ 
                                                    width: 12, 
                                                    height: 12, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: phraseColor,
                                                    mr: 2,
                                                    flexShrink: 0
                                                }} 
                                            />
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body1">
                                                            {phrase.phrase}
                                                        </Typography>
                                                        {isConflicting && (
                                                            <Chip 
                                                                icon={<WarningIcon />}
                                                                label="Conflict"
                                                                color="warning"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography
                                                        component="span"
                                                        onClick={(e) => handleTranslationClick(e, index)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            color: phrase.dictionaryPhraseId ? 'text.primary' : 'primary.main',
                                                            '&:hover': {
                                                                textDecoration: 'underline'
                                                            }
                                                        }}
                                                    >
                                                        {getTranslationForPhrase(phrase)}
                                                    </Typography>
                                                }
                                            />
                                            <IconButton 
                                                edge="end" 
                                                onClick={(e) => handleDeletePhrase(index, e)}
                                                sx={{ ml: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    </Grid>

                    {/* Right side - Dictionary */}
                    <Grid item xs={6} sx={{ height: '100%', overflow: 'auto' }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Phrases Dictionary
                            </Typography>
                            <List>
                                {Array.from(phrasesDictionary.entries()).map(([id, dictPhrase]) => {
                                    // Find phrases that map to this dictionary entry
                                    const mappedPhrases = phraseObjects
                                        .map((phrase, index) => ({ phrase, index }))
                                        .filter(({ phrase }) => phrase.dictionaryPhraseId === id);
                                    
                                    return (
                                        <Paper 
                                            key={id} 
                                            elevation={selectedPhraseIndex !== null && phraseObjects[selectedPhraseIndex]?.dictionaryPhraseId === id ? 3 : 1}
                                            sx={{ 
                                                mb: 2, 
                                                p: 2,
                                                cursor: selectedPhraseIndex !== null ? 'pointer' : 'default',
                                                bgcolor: selectedPhraseIndex !== null && phraseObjects[selectedPhraseIndex]?.dictionaryPhraseId === id 
                                                    ? 'action.selected' 
                                                    : 'background.paper',
                                                borderLeft: mappedPhrases.length > 0 ? 
                                                    `6px solid ${phraseColors.get(mappedPhrases[0].index)}` : 
                                                    '6px solid #e0e0e0',
                                                '&:hover': selectedPhraseIndex !== null ? {
                                                    bgcolor: 'action.hover'
                                                } : {}
                                            }}
                                            onClick={() => selectedPhraseIndex !== null && handleDictionaryItemSelect(id)}
                                        >
                                            {/* Color indicators for mapped phrases */}
                                            {mappedPhrases.length > 0 && (
                                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Mapped to:
                                                    </Typography>
                                                    {mappedPhrases.map(({ index }) => (
                                                        <Box 
                                                            key={index}
                                                            sx={{ 
                                                                width: 8, 
                                                                height: 8, 
                                                                borderRadius: '50%', 
                                                                backgroundColor: phraseColors.get(index),
                                                                border: conflictData.conflictingPhraseIndices.has(index) ? 
                                                                    '1px solid #ff5722' : 'none'
                                                            }} 
                                                            title={`Phrase ${index + 1}: ${phraseObjects[index].phrase}`}
                                                        />
                                                    ))}
                                                    {mappedPhrases.some(({ index }) => conflictData.conflictingPhraseIndices.has(index)) && (
                                                        <Chip 
                                                            icon={<WarningIcon />}
                                                            label="Conflict"
                                                            color="warning"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                            
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <input
                                                    type="text"
                                                    value={dictPhrase.phrase}
                                                    onChange={(e) => handlePhraseChange(e.target.value, id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        padding: '8px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                        marginBottom: '8px',
                                                        width: '100%'
                                                    }}
                                                />
                                                <IconButton 
                                                    size="small"
                                                    onClick={(e) => handleDeleteDictionaryEntry(id, e)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                component="div"
                                                sx={{ 
                                                    mt: 1,
                                                    cursor: 'text',
                                                    '&:hover': { bgcolor: 'background.default' }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <TranslationInput
                                                    phrase={dictPhrase.phrase}
                                                    initialValue={dictPhrase.translation}
                                                    dictionaryId={id}
                                                    onTranslationChange={handleDictionaryChange}
                                                />
                                            </Typography>
                                        </Paper>
                                    );
                                })}
                            </List>
                        </Box>
                    </Grid>
                </Grid>

                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => {
                        setMenuAnchorEl(null)
                        setPhraseIndexForMenu(null)
                    }}
                >
                    {Array.from(phrasesDictionary.entries()).map(([id, dictPhrase]) => (
                        <MenuItem 
                            key={id}
                            onClick={() => handleDictionaryItemSelect(id)}
                            sx={{ 
                                minWidth: '200px',
                                whiteSpace: 'normal'
                            }}
                        >
                            <ListItemText 
                                primary={dictPhrase.phrase}
                                secondary={dictPhrase.translation || "No translation"}
                            />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Floating action buttons for phrase creation */}
                {!isCreatingPhrase ? (
                    <Tooltip title="Create new phrase">
                        <Fab 
                            color="primary" 
                            sx={{ position: 'fixed', bottom: 16, right: 16 }}
                            onClick={() => setIsCreatingPhrase(true)}
                        >
                            <AddIcon />
                        </Fab>
                    </Tooltip>
                ) : (
                    <Box sx={{ position: 'fixed', bottom: 16, right: 16, display: 'flex', gap: 2 }}>
                        <Tooltip title="Cancel phrase creation">
                            <Fab 
                                color="secondary"
                                onClick={() => {
                                    setIsCreatingPhrase(false)
                                    setSelectedWordIndices([])
                                }}
                            >
                                <CancelIcon />
                            </Fab>
                        </Tooltip>
                        <Tooltip title="Save new phrase">
                            <span>
                                <Fab 
                                    color="primary" 
                                    onClick={createNewPhrase}
                                    disabled={selectedWordIndices.length === 0}
                                >
                                    <CheckIcon />
                                </Fab>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </FullScreenDialog>

            <TranslationPhraseEditorGuide 
                open={userGuideOpen}
                onClose={() => setUserGuideOpen(false)}
            />
        </>
    )
}

export default TranslationPhraseEditor 